import React, { useState, useEffect, useRef } from "react";
import { Edit2, Trash2, Upload } from "lucide-react";
import "./Brands.css";
import { adminApi } from "../../utils/adminApi";
import Papa from "papaparse";

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");

  const [brandInput, setBrandInput] = useState("");
  const [modelInput, setModelInput] = useState("");

  const [editingBrandId, setEditingBrandId] = useState(null);
  const [editingModelId, setEditingModelId] = useState(null);

  const [csvFile, setCsvFile] = useState(null);

  // IMAGE STATES
  const [brandImage, setBrandImage] = useState(null);
  const [brandPreview, setBrandPreview] = useState(null);

  const [modelImage, setModelImage] = useState(null);
  const [modelPreview, setModelPreview] = useState(null);

  const brandFileRef = useRef(null);
  const modelFileRef = useRef(null);
  const csvInputRef = useRef(null);

  useEffect(() => {
    fetchBrands();
    fetchModels();
  }, []);

  const fetchBrands = async () => {
    const res = await adminApi.get("/api/brands");
    const brandList = Array.isArray(res.data) ? res.data : [];
    setBrands(brandList);

    if (brandList.length === 0) {
      setSelectedBrand("");
      return;
    }

    const selectedExists = brandList.some(
      (brand) => String(brand?._id || "") === String(selectedBrand)
    );

    if (!selectedExists) {
      setSelectedBrand(brandList[0]._id);
    }
  };

  const fetchModels = async () => {
    const res = await adminApi.get("/api/models");
    setModels(Array.isArray(res.data) ? res.data : []);
  };

  const fileToBase64 = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });

  /* ================= BRAND ================= */

  const saveBrand = async () => {
    if (!brandInput.trim()) return;

    const logoBase64 = brandImage ? await fileToBase64(brandImage) : "";

    if (editingBrandId) {
      await adminApi.put(`/api/brands/${editingBrandId}`, {
        name: brandInput.trim(),
        logo: logoBase64,
      });
      setEditingBrandId(null);
    } else {
      await adminApi.post("/api/brands", {
        name: brandInput.trim(),
        logo: logoBase64,
      });
    }

    setBrandInput("");
    setBrandImage(null);
    setBrandPreview(null);
    fetchBrands();
  };

  const editBrand = (brand) => {
    setBrandInput(brand.name);
    setBrandPreview(brand.logo || null);
    setEditingBrandId(brand._id);
  };

  const deleteBrand = async (id) => {
    await adminApi.delete(`/api/brands/${id}`);
    fetchBrands();
    fetchModels();
  };

  /* ================= MODEL ================= */

  const saveModel = async () => {
    if (!modelInput.trim() || !selectedBrand) return;

    const imageBase64 = modelImage ? await fileToBase64(modelImage) : "";

    if (editingModelId) {
      await adminApi.put(`/api/models/${editingModelId}`, {
        name: modelInput.trim(),
        brandId: selectedBrand,
        image: imageBase64,
      });
      setEditingModelId(null);
    } else {
      await adminApi.post("/api/models", {
        name: modelInput.trim(),
        brandId: selectedBrand,
        image: imageBase64,
      });
    }

    setModelInput("");
    setModelImage(null);
    setModelPreview(null);
    fetchModels();
  };

  const editModel = (model) => {
    setModelInput(model.name);
    setModelPreview(model.image || null);
    setEditingModelId(model._id);
  };

  const deleteModel = async (id) => {
    await adminApi.delete(`/api/models/${id}`);
    fetchModels();
  };

  /* ================= CSV UPLOAD ================= */
  const uploadCsv = () => {
    if (!csvFile) return alert("Please select a CSV file");

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) =>
        String(header || "")
          .replace(/\uFEFF/g, "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_"),
      complete: async (result) => {
        try {
          const formattedData = result.data
            .map((item) => {
              const name = String(
                item.name || item.model || item.model_name || item.modelname || ""
              ).trim();
              const brand = String(
                item.brand ||
                  item.brand_name ||
                  item.brandname ||
                  item.company ||
                  ""
              ).trim();

              return { name, brand };
            })
            .filter((item) => item.name && item.brand);

          if (formattedData.length === 0) {
            alert("No valid rows found. CSV must include brand + model/name columns.");
            return;
          }

          const res = await adminApi.post("/api/models/bulk", formattedData);
          const createdCount = Number(res?.data?.count || 0);

          if (createdCount <= 0) {
            alert("Upload processed, but no rows were imported. Check CSV columns.");
            return;
          }

          alert(`Upload successful. Imported ${createdCount} row(s).`);

          setCsvFile(null);
          if (csvInputRef.current) csvInputRef.current.value = "";
          await fetchModels();
          await fetchBrands();
        } catch (err) {
          alert("Upload failed. Please verify CSV format and try again.");
        }
      }
    });
  };

  /* ================= DRAG DROP ================= */
  const handleDrop = (e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (type === "brand") {
      setBrandImage(file);
      setBrandPreview(URL.createObjectURL(file));
    } else {
      setModelImage(file);
      setModelPreview(URL.createObjectURL(file));
    }
  };

  return (
    <div className="inventory-page">
      <h1 style={{ color: 'black' }}>Brand & Model Management</h1>

      <div className="split-layout equal-panels">
        {/* ================= BRANDS PANEL ================= */}
        <div className="panel">
          <h3 style={{ color: 'black' }}>Brands</h3>

          <div
            className="drag-area"
            onClick={() => brandFileRef.current.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, "brand")}
          >
            {brandPreview ? (
              <img src={brandPreview} className="preview-img" alt="Brand" />
            ) : (
              <>
                <Upload size={24} style={{ color: "#999" }} />
                <p style={{ color: 'black' }}>Drag Brand Logo Here</p>
              </>
            )}
          </div>

          <input
            type="file"
            hidden
            ref={brandFileRef}
            accept="image/*"
            onChange={(e) => {
              setBrandImage(e.target.files[0]);
              setBrandPreview(URL.createObjectURL(e.target.files[0]));
            }}
          />

          <input
            type="text"
            placeholder="Brand Name"
            value={brandInput}
            onChange={(e) => setBrandInput(e.target.value)}
            className="text-input"
            style={{ color: 'black' }}
          />

          <button className="primary-btn" onClick={saveBrand}>
            {editingBrandId ? "Update Brand" : "Add Brand"}
          </button>

          <ul className="brand-list">
            {brands.map((b) => (
              <li
                key={b._id}
                className={
                  selectedBrand === b._id ? "brand-item active" : "brand-item"
                }
                onClick={() => setSelectedBrand(b._id)}
              >
                <div className="item-content">
                  <span className="item-name">{b.name}</span>
                  <div className="action-icons">
                    <button
                      className="icon-btn edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        editBrand(b);
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="icon-btn delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteBrand(b._id);
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* ================= MODELS PANEL ================= */}
        <div className="panel">
          <h3 style={{ color: 'black' }}>Models</h3>

          <div
            className="drag-area"
            onClick={() => modelFileRef.current.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, "model")}
          >
            {modelPreview ? (
              <img src={modelPreview} className="preview-img" alt="Model" />
            ) : (
              <>
                <Upload size={24} style={{ color: "#999" }} />
                <p style={{ color: 'black' }}>Drag Model Image Here</p>
              </>
            )}
          </div>

          <input
            type="file"
            hidden
            ref={modelFileRef}
            accept="image/*"
            onChange={(e) => {
              setModelImage(e.target.files[0]);
              setModelPreview(URL.createObjectURL(e.target.files[0]));
            }}

          />

          <input
            type="text"
            placeholder="Model Name"
            value={modelInput}
            onChange={(e) => setModelInput(e.target.value)}
            className="text-input"
            style={{ color: 'black' }}
          />

          <button className="primary-btn" onClick={saveModel}>
            {editingModelId ? "Update Model" : "Add Model"}
          </button>

          <ul className="model-list">
            {models
              .filter((m) => String(m.brandId?._id || m.brandId) === String(selectedBrand))
              .map((m) => (
                <li key={m._id} className="model-item">
                  <div className="item-content">
                    <span className="item-name">{m.name}</span>
                    <div className="action-icons">
                      <button className="icon-btn edit" onClick={() => editModel(m)}>
                        <Edit2 size={16} />
                      </button>
                      <button className="icon-btn delete" onClick={() => deleteModel(m._id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
          </ul>
        </div>
      </div>

      {/* ================= CSV UPLOAD ================= */}
      <div className="csv-section">
        <h3 style={{ color: 'black' }}>Bulk Upload Brands & Models (CSV)</h3>
        <div className="csv-upload-row">
          <input
            type="file"
            accept=".csv"
            ref={csvInputRef}
            className="csv-input"
            onChange={(e) => setCsvFile(e.target.files[0])}
          />
          <p className="csv-file-name">{csvFile ? csvFile.name : "No CSV selected"}</p>
        </div>
        <button className="primary-btn" onClick={uploadCsv} >
          Upload CSV
        </button>
      </div>
    </div>
  );
};

export default Brands;
